import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  createMultilingualString,
  updateMultilingualString,
  fetchMultilingualStrings,
  fetchAvailableLanguages,
} from "../../redux/slices/multilingualStringSlice";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { useToast } from "../../components/ui/use-toast";
import { Badge } from "../../components/ui/badge";
import { Plus, X, Copy, Upload, Download, Zap } from "lucide-react";

const MultilingualStringDialog = ({ open, onClose, multilingualString, defaultTab = "single" }) => {
  const dispatch = useDispatch();
  const { toast } = useToast();
  const { availableLanguages, multilingualStrings } = useSelector(
    (state) => state.multilingualString
  );

  const [activeTab, setActiveTab] = useState("single");
  const [formData, setFormData] = useState({
    key: "",
    translations: {},
    category: "",
    description: "",
  });

  const [selectedLanguages, setSelectedLanguages] = useState(["en"]);
  const [bulkData, setBulkData] = useState("");
  const [templates] = useState([
    { key: "welcome_message", category: "common", description: "Welcome message displayed on home screen" },
    { key: "login_button", category: "auth", description: "Text for login button" },
    { key: "register_button", category: "auth", description: "Text for register button" },
    { key: "home_title", category: "navigation", description: "Title for home screen" },
    { key: "settings_title", category: "navigation", description: "Title for settings screen" },
    { key: "submit_button", category: "common", description: "Generic submit button text" },
    { key: "cancel_button", category: "common", description: "Generic cancel button text" },
    { key: "loading_message", category: "common", description: "Loading indicator message" },
    { key: "error_message", category: "common", description: "Generic error message" },
  ]);

  useEffect(() => {
    if (open) {
      dispatch(fetchAvailableLanguages());
    }
  }, [open, dispatch]);

  useEffect(() => {
    if (multilingualString) {
      setFormData({
        key: multilingualString.key || "",
        translations: multilingualString.translations || {},
        category: multilingualString.category || "",
        description: multilingualString.description || "",
      });
      setSelectedLanguages(Object.keys(multilingualString.translations || {}));
      setActiveTab("single");
    } else {
      resetForm();
    }
  }, [multilingualString]);

  const resetForm = () => {
    setFormData({
      key: "",
      translations: {},
      category: "",
      description: "",
    });
    setSelectedLanguages(["en"]);
    setBulkData("");
    setActiveTab(defaultTab);
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleTranslationChange = (language, value) => {
    setFormData((prev) => ({
      ...prev,
      translations: {
        ...prev.translations,
        [language]: value,
      },
    }));
  };

  const addLanguage = (language) => {
    if (!selectedLanguages.includes(language)) {
      setSelectedLanguages((prev) => [...prev, language]);
    }
  };

  const removeLanguage = (language) => {
    if (selectedLanguages.length > 1) {
      setSelectedLanguages((prev) => prev.filter((lang) => lang !== language));
      setFormData((prev) => {
        const newTranslations = { ...prev.translations };
        delete newTranslations[language];
        return {
          ...prev,
          translations: newTranslations,
        };
      });
    }
  };

  const addAllLanguages = () => {
    setSelectedLanguages([...availableLanguages]);
  };

  const copyTranslation = (fromLang, toLang) => {
    if (formData.translations[fromLang]) {
      setFormData((prev) => ({
        ...prev,
        translations: {
          ...prev.translations,
          [toLang]: prev.translations[fromLang],
        },
      }));
    }
  };

  const applyTemplate = (template) => {
    setFormData((prev) => ({
      ...prev,
      key: template.key,
      category: template.category,
      description: template.description,
    }));
  };

  const handleBulkImport = () => {
    try {
      const data = JSON.parse(bulkData);
      if (Array.isArray(data)) {
        // Handle array of strings
        data.forEach((item) => {
          if (item.key && item.translations) {
            dispatch(createMultilingualString(item));
          }
        });
        toast({
          title: "Success",
          description: `${data.length} strings imported successfully`,
        });
        dispatch(fetchMultilingualStrings());
        onClose();
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Invalid JSON format",
        variant: "destructive",
      });
    }
  };

  const exportStrings = async () => {
    try {
      let strings = multilingualStrings;
      if (!strings?.length) {
        strings = await dispatch(fetchMultilingualStrings()).unwrap();
      }
      const dataStr = JSON.stringify(strings, null, 2);
      const dataUri =
        "data:application/json;charset=utf-8," + encodeURIComponent(dataStr);
      const linkElement = document.createElement("a");
      linkElement.setAttribute("href", dataUri);
      linkElement.setAttribute("download", "multilingual-strings.json");
      linkElement.click();
      toast({
        title: "Success",
        description: "Strings exported successfully",
      });
    } catch {
      toast({
        title: "Error",
        description: "Could not export strings",
        variant: "destructive",
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.key.trim()) {
      toast({
        title: "Error",
        description: "String key is required",
        variant: "destructive",
      });
      return;
    }

    if (!formData.category.trim()) {
      toast({
        title: "Error",
        description: "Category is required",
        variant: "destructive",
      });
      return;
    }

    if (Object.keys(formData.translations).length === 0) {
      toast({
        title: "Error",
        description: "At least one translation is required",
        variant: "destructive",
      });
      return;
    }

    // Check if all selected languages have translations
    const missingTranslations = selectedLanguages.filter(
      (lang) => !formData.translations[lang]?.trim()
    );

    if (missingTranslations.length > 0) {
      toast({
        title: "Error",
        description: `Please provide translations for: ${missingTranslations.join(
          ", "
        )}`,
        variant: "destructive",
      });
      return;
    }

    try {
      if (multilingualString) {
        await dispatch(
          updateMultilingualString({
            key: multilingualString.key,
            data: formData,
          })
        ).unwrap();
        toast({
          title: "Success",
          description: "Multilingual string updated successfully",
        });
      } else {
        await dispatch(createMultilingualString(formData)).unwrap();
        toast({
          title: "Success",
          description: "Multilingual string created successfully",
        });
      }

      dispatch(fetchMultilingualStrings());
      onClose();
    } catch (error) {
      toast({
        title: "Error",
        description: error || "Failed to save multilingual string",
        variant: "destructive",
      });
    }
  };

  const getLanguageName = (code) => {
    const languageNames = {
      en: "English",
      ar: "العربية",
      ur: "اردو",
      fr: "Français",
      es: "Español",
    };
    return languageNames[code] || code.toUpperCase();
  };

  const availableLanguagesToAdd = availableLanguages.filter(
    (lang) => !selectedLanguages.includes(lang)
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {multilingualString
              ? "Edit Multilingual String"
              : "Manage Multilingual Strings"}
          </DialogTitle>
        </DialogHeader>

        {!multilingualString && (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="single">Quick Add</TabsTrigger>
              <TabsTrigger value="template">Templates</TabsTrigger>
              <TabsTrigger value="bulk">Bulk Import</TabsTrigger>
              <TabsTrigger value="export">Export</TabsTrigger>
            </TabsList>

            <TabsContent value="single" className="space-y-6">
              <SingleStringForm
                formData={formData}
                selectedLanguages={selectedLanguages}
                availableLanguagesToAdd={availableLanguagesToAdd}
                handleInputChange={handleInputChange}
                handleTranslationChange={handleTranslationChange}
                addLanguage={addLanguage}
                removeLanguage={removeLanguage}
                addAllLanguages={addAllLanguages}
                copyTranslation={copyTranslation}
                getLanguageName={getLanguageName}
                onSubmit={handleSubmit}
                onClose={onClose}
                isEditing={!!multilingualString}
              />
            </TabsContent>

            <TabsContent value="template" className="space-y-6">
              <TemplateForm
                templates={templates}
                applyTemplate={applyTemplate}
                formData={formData}
                selectedLanguages={selectedLanguages}
                availableLanguagesToAdd={availableLanguagesToAdd}
                handleInputChange={handleInputChange}
                handleTranslationChange={handleTranslationChange}
                addLanguage={addLanguage}
                removeLanguage={removeLanguage}
                addAllLanguages={addAllLanguages}
                copyTranslation={copyTranslation}
                getLanguageName={getLanguageName}
                onSubmit={handleSubmit}
                onClose={onClose}
              />
            </TabsContent>

            <TabsContent value="bulk" className="space-y-6">
              <BulkImportForm
                bulkData={bulkData}
                setBulkData={setBulkData}
                onSubmit={handleSubmit}
                onClose={onClose}
              />
            </TabsContent>

            <TabsContent value="export" className="space-y-6">
              <ExportForm
                onExport={exportStrings}
                onClose={onClose}
              />
            </TabsContent>
          </Tabs>
        )}

        {multilingualString && (
          <SingleStringForm
            formData={formData}
            selectedLanguages={selectedLanguages}
            availableLanguagesToAdd={availableLanguagesToAdd}
            handleInputChange={handleInputChange}
            handleTranslationChange={handleTranslationChange}
            addLanguage={addLanguage}
            removeLanguage={removeLanguage}
            addAllLanguages={addAllLanguages}
            copyTranslation={copyTranslation}
            getLanguageName={getLanguageName}
            onSubmit={handleSubmit}
            onClose={onClose}
            isEditing={true}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

// Single String Form Component
const SingleStringForm = ({
  formData,
  selectedLanguages,
  availableLanguagesToAdd,
  handleInputChange,
  handleTranslationChange,
  addLanguage,
  removeLanguage,
  addAllLanguages,
  copyTranslation,
  getLanguageName,
  onSubmit,
  onClose,
  isEditing,
}) => (
  <form onSubmit={onSubmit} className="space-y-6">
    {/* Basic Information */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="space-y-2">
        <Label htmlFor="key">String Key *</Label>
        <Input
          id="key"
          value={formData.key}
          onChange={(e) => handleInputChange("key", e.target.value)}
          placeholder="e.g., welcome_message"
          disabled={isEditing}
        />
        <p className="text-sm text-muted-foreground">
          Unique identifier for this string
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="category">Category *</Label>
        <Select
          value={formData.category}
          onValueChange={(value) => handleInputChange("category", value)}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="common">Common</SelectItem>
            <SelectItem value="auth">Authentication</SelectItem>
            <SelectItem value="navigation">Navigation</SelectItem>
            <SelectItem value="quiz">Quiz</SelectItem>
            <SelectItem value="profile">Profile</SelectItem>
            <SelectItem value="settings">Settings</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>

    <div className="space-y-2">
      <Label htmlFor="description">Description</Label>
      <Textarea
        id="description"
        value={formData.description}
        onChange={(e) => handleInputChange("description", e.target.value)}
        placeholder="Optional description of this string's purpose"
        rows={2}
      />
    </div>

    {/* Language Management */}
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label>Languages</Label>
        <div className="flex gap-2">
          {availableLanguagesToAdd.length > 0 && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addAllLanguages}
            >
              <Zap className="h-4 w-4 mr-1" />
              Add All
            </Button>
          )}
          {availableLanguagesToAdd.length > 0 && (
            <Select onValueChange={addLanguage}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Add language" />
              </SelectTrigger>
              <SelectContent>
                {availableLanguagesToAdd.map((lang) => (
                  <SelectItem key={lang} value={lang}>
                    {getLanguageName(lang)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {/* Translation Inputs */}
      <div className="grid gap-4">
        {selectedLanguages.map((language) => (
          <div key={language} className="border rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-base font-medium">
                {getLanguageName(language)} ({language.toUpperCase()})
              </Label>
              <div className="flex gap-2">
                {selectedLanguages.length > 1 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeLanguage(language)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
                {selectedLanguages.filter(l => l !== language).length > 0 && (
                  <Select
                    onValueChange={(fromLang) => copyTranslation(fromLang, language)}
                  >
                    <SelectTrigger className="w-32">
                      <SelectValue placeholder="Copy from" />
                    </SelectTrigger>
                    <SelectContent>
                      {selectedLanguages.filter(l => l !== language).map((lang) => (
                        <SelectItem key={lang} value={lang}>
                          {getLanguageName(lang)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>
            <Textarea
              value={formData.translations[language] || ""}
              onChange={(e) =>
                handleTranslationChange(language, e.target.value)
              }
              placeholder={`Enter translation in ${getLanguageName(language)}`}
              rows={2}
              required
            />
          </div>
        ))}
      </div>
    </div>

    {/* Form Actions */}
    <div className="flex justify-end space-x-2 pt-4">
      <Button type="button" variant="outline" onClick={onClose}>
        Cancel
      </Button>
      <Button type="submit">
        {isEditing ? "Update" : "Create"} String
      </Button>
    </div>
  </form>
);

// Template Form Component
const TemplateForm = ({
  templates,
  applyTemplate,
  formData,
  selectedLanguages,
  availableLanguagesToAdd,
  handleInputChange,
  handleTranslationChange,
  addLanguage,
  removeLanguage,
  addAllLanguages,
  copyTranslation,
  getLanguageName,
  onSubmit,
  onClose,
}) => (
  <div className="space-y-6">
    <div>
      <Label className="text-base font-medium">Choose a Template</Label>
      <p className="text-sm text-muted-foreground mb-4">
        Select a common string template to get started quickly
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {templates.map((template) => (
          <Button
            key={template.key}
            type="button"
            variant="outline"
            className="justify-start h-auto p-3"
            onClick={() => applyTemplate(template)}
          >
            <div className="text-left">
              <div className="font-medium">{template.key}</div>
              <div className="text-sm text-muted-foreground">
                {template.description}
              </div>
              <Badge className="mt-1" variant="secondary">
                {template.category}
              </Badge>
            </div>
          </Button>
        ))}
      </div>
    </div>

    <SingleStringForm
      formData={formData}
      selectedLanguages={selectedLanguages}
      availableLanguagesToAdd={availableLanguagesToAdd}
      handleInputChange={handleInputChange}
      handleTranslationChange={handleTranslationChange}
      addLanguage={addLanguage}
      removeLanguage={removeLanguage}
      addAllLanguages={addAllLanguages}
      copyTranslation={copyTranslation}
      getLanguageName={getLanguageName}
      onSubmit={onSubmit}
      onClose={onClose}
      isEditing={false}
    />
  </div>
);

// Bulk Import Form Component
const BulkImportForm = ({ bulkData, setBulkData, onSubmit, onClose }) => (
  <div className="space-y-6">
    <div>
      <Label className="text-base font-medium">Bulk Import JSON</Label>
      <p className="text-sm text-muted-foreground mb-4">
        Paste a JSON array of multilingual strings to import multiple strings at once
      </p>
      <Textarea
        value={bulkData}
        onChange={(e) => setBulkData(e.target.value)}
        placeholder={`[
  {
    "key": "welcome_message",
    "translations": {
      "en": "Welcome to Iqrah",
      "ar": "مرحباً بك في إقرأ"
    },
    "category": "common",
    "description": "Welcome message"
  }
]`}
        rows={15}
        className="font-mono text-sm"
      />
    </div>

    <div className="flex justify-end space-x-2">
      <Button type="button" variant="outline" onClick={onClose}>
        Cancel
      </Button>
      <Button type="submit" onClick={onSubmit}>
        <Upload className="h-4 w-4 mr-2" />
        Import Strings
      </Button>
    </div>
  </div>
);

// Export Form Component
const ExportForm = ({ onExport, onClose }) => (
  <div className="space-y-6">
    <div className="text-center py-8">
      <Download className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
      <h3 className="text-lg font-medium">Export Multilingual Strings</h3>
      <p className="text-sm text-muted-foreground mb-6">
        Download all multilingual strings as a JSON file for backup or external editing
      </p>
      <div className="flex justify-center gap-4">
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={onExport}>
          <Download className="h-4 w-4 mr-2" />
          Export JSON
        </Button>
      </div>
    </div>
  </div>
);

export default MultilingualStringDialog;
